import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { login, signInWithGoogle } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/hooks/useToast'
import { Logo } from '@/components/Logo'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const { showToast } = useToast()
  const { setSession } = useAuthStore()

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard'

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true)
    try {
      await signInWithGoogle()
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Google sign-in failed', 'error')
      setGoogleLoading(false)
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!email || !password) {
      showToast('Please fill in all fields', 'error')
      return
    }

    setLoading(true)

    try {
      const data = await login(email, password)
      setSession(data.session)
      showToast('Welcome back!')
      navigate(from, { replace: true })
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Login failed', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      {/* Left Branding Section */}
      <section className="relative hidden lg:flex flex-col justify-between w-[45%] p-12 text-white overflow-hidden bg-gradient-to-br from-[#1754cf] to-[#0a2358]">
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-80 h-80 bg-blue-400/20 rounded-full blur-3xl" />

        {/* Logo */}
        <div className="relative z-10">
          <Logo size="lg" variant="light" />
        </div>

        {/* Testimonial */}
        <div className="relative z-10 max-w-md">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-semibold uppercase tracking-wider mb-6">
              <span className="material-symbols-outlined text-sm">auto_awesome</span>
              New: Advanced Analytics
            </div>
            <h2 className="text-4xl font-bold leading-[1.2] mb-6">
              Shorten links, <br />
              <span className="text-blue-200">expand your reach.</span>
            </h2>
            <p className="text-blue-100 text-lg leading-relaxed">
              "URLzippy has completely transformed how we track our marketing campaigns. The granular data is
              a game changer for our growth team."
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full border-2 border-white/30 bg-gradient-to-tr from-indigo-400 to-purple-500" />
            <div>
              <p className="font-semibold">Sarah Jenkins</p>
              <p className="text-sm text-blue-200">Director of Growth @ TechStream</p>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="relative z-10 flex gap-8 text-blue-200 text-sm">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base">verified</span>
            <span>SOC2 Compliant</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base">public</span>
            <span>Global Edge Network</span>
          </div>
        </div>
      </section>

      {/* Right Form Section */}
      <main className="flex-1 flex flex-col justify-center items-center px-6 py-12 lg:px-24 bg-white dark:bg-[#101622]">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-12">
            <Logo size="lg" variant="auto" />
          </div>

          {/* Header */}
          <div className="mb-10 text-left">
            <h1 className="text-3xl font-bold text-[#0d121b] dark:text-white tracking-tight mb-2">
              Welcome back
            </h1>
            <p className="text-[#4c669a] dark:text-gray-400">Sign in to manage your short links.</p>
          </div>

          {/* Google Sign In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            className="cursor-pointer w-full h-12 bg-white dark:bg-white/5 border border-[#cfd7e7] dark:border-white/10 rounded-xl font-semibold text-sm text-[#0d121b] dark:text-white transition-all hover:border-[#9baec8] dark:hover:border-white/20 hover:shadow-sm disabled:opacity-50 flex items-center justify-center gap-3 mb-6"
          >
            {googleLoading ? (
              <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
                <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
              </svg>
            )}
            {googleLoading ? 'Connecting...' : 'Continue with Google'}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-[#e7ebf3] dark:bg-white/10" />
            <span className="text-xs font-medium text-[#4c669a]">or continue with email</span>
            <div className="flex-1 h-px bg-[#e7ebf3] dark:bg-white/10" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#0d121b] dark:text-white mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full h-11 px-4 rounded-lg bg-white dark:bg-white/5 border border-[#cfd7e7] dark:border-white/10 text-[#0d121b] dark:text-white text-sm placeholder:text-[#9baec8] dark:placeholder:text-gray-500 focus:border-[#1754cf] focus:ring-2 focus:ring-[#1754cf]/20 focus:outline-none hover:border-[#9baec8] dark:hover:border-white/20 transition-colors duration-150"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm text-[#1754cf] hover:underline font-medium">
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full h-11 px-4 rounded-lg bg-white dark:bg-white/5 border border-[#cfd7e7] dark:border-white/10 text-[#0d121b] dark:text-white text-sm placeholder:text-[#9baec8] dark:placeholder:text-gray-500 focus:border-[#1754cf] focus:ring-2 focus:ring-[#1754cf]/20 focus:outline-none hover:border-[#9baec8] dark:hover:border-white/20 transition-colors duration-150"
                autoComplete="current-password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="cursor-pointer w-full h-11 bg-[#1754cf] hover:opacity-90 active:opacity-80 text-white rounded-lg font-semibold text-sm transition-all shadow-[0_2px_10px_0_rgba(23,84,207,0.3)] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Sign up link */}
          <p className="mt-8 text-center text-[#4c669a] dark:text-gray-400">
            Don't have an account?{' '}
            <Link to="/signup" className="text-[#1754cf] hover:underline font-semibold">
              Create one free
            </Link>
          </p>
        </div>
      </main>
    </div>
  )
}
